import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, MapPin, Plus, Save, Settings, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/app/AppShell";
import ManagePositionsModal from "@/components/app/ManagePositionsModal";
import PhotoGallery from "@/components/app/PhotoGallery";
import SignaturePad from "@/components/app/SignaturePad";
import WorkItemCard from "@/components/app/WorkItemCard";
import WorkItemSheet from "@/components/app/WorkItemSheet";
import CCChipsInput from "@/components/app/CCChipsInput";
import { useIsMobile } from "@/hooks/useMediaQuery";

const dayFromDate = (d) => {
    if (!d) return "";
    try {
        return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long" });
    } catch {
        return "";
    }
};

const GEO_DENIED_KEY = "isp:geo-denied";

const WMO_MAP = {
    0: "Clear", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast",
    45: "Fog", 48: "Fog",
    51: "Drizzle", 53: "Drizzle", 55: "Drizzle",
    56: "Freezing drizzle", 57: "Freezing drizzle",
    61: "Light rain", 63: "Rain", 65: "Heavy rain",
    66: "Freezing rain", 67: "Freezing rain",
    71: "Light snow", 73: "Snow", 75: "Heavy snow", 77: "Snow grains",
    80: "Rain showers", 81: "Rain showers", 82: "Rain showers",
    85: "Snow showers", 86: "Snow showers",
    95: "Thunderstorm", 96: "Thunderstorm with hail", 99: "Thunderstorm with hail",
};

const getCurrentPosition = (opts) =>
    new Promise((resolve, reject) => {
        if (!("geolocation" in navigator)) {
            reject(new Error("Geolocation not supported"));
            return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, opts);
    });

const todayISO = () => new Date().toISOString().slice(0, 10);

const SectionCard = ({ title, subtitle, right, children, className = "", collapsible = false, defaultOpen = true, testId, ...rest }) => {
    const isMobile = useIsMobile();
    const [open, setOpen] = useState(defaultOpen);
    const showChildren = !isMobile || !collapsible || open;
    return (
        <section className={`glass-card ${className}`} data-testid={testId} {...rest}>
            {isMobile && collapsible ? (
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="section-toggle mb-3"
                    aria-expanded={open}
                    data-testid={testId ? `${testId}-toggle` : undefined}
                >
                    <div>
                        <div className="section-title">{title}</div>
                        {subtitle && <div className="section-subtitle">{subtitle}</div>}
                    </div>
                    <ChevronDown className="section-chevron" size={20} data-open={open} strokeWidth={2} />
                </button>
            ) : (
                <div className="mb-5 flex items-start justify-between gap-3">
                    <div>
                        <div className="section-title">{title}</div>
                        {subtitle && <div className="section-subtitle">{subtitle}</div>}
                    </div>
                    {right}
                </div>
            )}
            {showChildren && children}
        </section>
    );
};

const ReportEditPage = () => {
    const { id } = useParams();
    const nav = useNavigate();

    const [report, setReport] = useState(null);
    const [positions, setPositions] = useState([]);
    const [suggestions, setSuggestions] = useState({
        recent_pm_emails: [], recent_employee_names: [], recent_cc_emails: [],
    });
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [positionsModalOpen, setPositionsModalOpen] = useState(false);
    const [certifyErrors, setCertifyErrors] = useState({});
    const [certifying, setCertifying] = useState(false);
    const [manualDraftSaving, setManualDraftSaving] = useState(false);
    const [geoDenied, setGeoDenied] = useState(() => {
        try { return sessionStorage.getItem(GEO_DENIED_KEY) === "1"; } catch { return false; }
    });
    const [weatherFetching, setWeatherFetching] = useState(false);
    const [dateChangedSinceWeather, setDateChangedSinceWeather] = useState(false);
    const [expandedItemId, setExpandedItemId] = useState(null); // work item id or "new:<uuid>"
    const [focusTitleId, setFocusTitleId] = useState(null);
    const [sheetItem, setSheetItem] = useState(null); // { index, item, isNew } | null
    const isMobile = useIsMobile();
    const sigRef = useRef(null);
    const [sigDataUrl, setSigDataUrl] = useState("");
    const [checkbox, setCheckbox] = useState(false);
    const autoWeatherTriedRef = useRef(false);
    const reportRef = useRef(null);

    const loadPositions = useCallback(async () => {
        try {
            const { data } = await api.get("/positions");
            setPositions(data);
        } catch { /* non-blocking */ }
    }, []);

    const loadSuggestions = useCallback(async () => {
        try {
            const { data } = await api.get("/me/suggestions");
            setSuggestions(data);
        } catch { /* non-blocking */ }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/reports/${id}`);
                if (data.status === "certified") {
                    nav(`/reports/${id}`, { replace: true });
                    return;
                }
                setReport(data);
                reportRef.current = data;
                setCheckbox(!!data.certified_checkbox);
                if (data.signature_png_base64) setSigDataUrl(data.signature_png_base64);
            } catch (err) {
                if (err?.response?.status === 404) {
                    nav("/reports", { replace: true });
                    return;
                }
                setError(formatApiError(err));
            }
        })();
        loadPositions();
        loadSuggestions();
    }, [id, nav, loadPositions, loadSuggestions]);

    const set = (updater) =>
        setReport((prev) => {
            if (!prev) return prev;
            const next = updater(structuredClone(prev));
            reportRef.current = next;
            return next;
        });

    const save = useCallback(
        async (patch) => {
            setSaving(true);
            setError("");
            try {
                const { data } = await api.patch(`/reports/${id}`, patch);
                setReport((prev) => {
                    const base = prev || data;
                    const employees = (base.employees || []).map((row, i) => {
                        const srv = (data.employees || [])[i];
                        if (!srv) return row;
                        return { ...row, total_hours: srv.total_hours, warning: srv.warning };
                    });
                    // Server is authoritative for work_items (assigns ids on new items)
                    const merged = {
                        ...base,
                        day_of_week: data.day_of_week,
                        crew_total_hours: data.crew_total_hours,
                        updated_at: data.updated_at,
                        status: data.status,
                        employees,
                        work_items: data.work_items,
                        cover_photos: data.cover_photos,
                    };
                    reportRef.current = merged;
                    return merged;
                });
                setSavedAt(new Date());
                // refresh suggestions after any save (name/email might have been added)
                loadSuggestions();
                return { ok: true, data };
            } catch (err) {
                setError(formatApiError(err));
                return { ok: false, error: formatApiError(err) };
            } finally {
                setSaving(false);
            }
        },
        [id, loadSuggestions]
    );

    const saveField = (path, value) => save(pathToPatch(path, value));

    // ---------- Weather auto-fetch (date-aware) ----------
    const applyWeatherResponse = useCallback(
        async (label, temperature, unit) => {
            const rounded = typeof temperature === "number" ? Math.round(temperature * 10) / 10 : null;
            const patch = { weather: label, temperature: { value: rounded, unit } };
            set((r) => { r.weather = patch.weather; r.temperature = patch.temperature; return r; });
            await save(patch);
            setDateChangedSinceWeather(false);
        },
        [save]
    );

    const fetchWeather = useCallback(
        async ({ manual }) => {
            const cur = reportRef.current;
            const unit = cur?.temperature?.unit || "F";
            const tempUnit = unit === "C" ? "celsius" : "fahrenheit";
            const rdate = (cur?.report_date || "").trim();
            const useArchive = rdate && rdate < todayISO();
            setWeatherFetching(true);
            try {
                const pos = await getCurrentPosition({
                    enableHighAccuracy: false, timeout: 10000, maximumAge: 60000,
                });
                const lat = pos.coords.latitude, lon = pos.coords.longitude;
                let url;
                if (useArchive) {
                    url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${rdate}&end_date=${rdate}&daily=weather_code,temperature_2m_mean&temperature_unit=${tempUnit}`;
                } else {
                    url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&temperature_unit=${tempUnit}`;
                }
                const res = await fetch(url);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                let temp, code;
                if (useArchive) {
                    temp = json?.daily?.temperature_2m_mean?.[0];
                    code = json?.daily?.weather_code?.[0];
                } else {
                    temp = json?.current?.temperature_2m;
                    code = json?.current?.weather_code;
                }
                const label = WMO_MAP[code] ?? "";
                await applyWeatherResponse(label, temp, unit);
                toast.success("Weather updated", { duration: 1500 });
            } catch (err) {
                if (err && (err.code === 1 || err.PERMISSION_DENIED === 1)) {
                    try { sessionStorage.setItem(GEO_DENIED_KEY, "1"); } catch {}
                    setGeoDenied(true);
                    if (manual) toast.error("Location permission denied — enter weather manually", { duration: 2000 });
                    return;
                }
                if (manual) toast.error("Couldn't fetch weather — enter manually", { duration: 2000 });
            } finally {
                setWeatherFetching(false);
            }
        },
        [applyWeatherResponse]
    );

    useEffect(() => {
        if (!report) return;
        if (autoWeatherTriedRef.current) return;
        if (geoDenied) return;
        const emptyWeather = !report.weather;
        const emptyTemp = report.temperature == null || report.temperature.value == null;
        if (!emptyWeather || !emptyTemp) return;
        autoWeatherTriedRef.current = true;
        fetchWeather({ manual: false });
    }, [report, geoDenied, fetchWeather]);

    // ---------- Employees ----------
    // Client-side "locked" flags — parallel array. Locked = user manually
    // edited that cell, so time-based auto-fill won't overwrite.
    const [empLocks, setEmpLocks] = useState([]); // [{reg:bool, ot:bool}, ...]
    const [empHint, setEmpHint] = useState(null); // {idx, text}

    const ensureLocks = (nRows) => {
        if (empLocks.length !== nRows) {
            const next = Array(nRows).fill(null).map((_, i) => empLocks[i] || { reg: false, ot: false });
            setEmpLocks(next);
            return next;
        }
        return empLocks;
    };

    const addRow = () => {
        set((r) => {
            r.employees = [
                ...(r.employees || []),
                { position: "", name: "", time_in: "", time_out: "",
                  regular_hours: null, overtime_hours: null },
            ];
            return r;
        });
        setEmpLocks((prev) => [...prev, { reg: false, ot: false }]);
    };
    const removeRow = (idx) => {
        const next = (report.employees || []).filter((_, i) => i !== idx);
        set((r) => { r.employees = next; return r; });
        setEmpLocks((prev) => prev.filter((_, i) => i !== idx));
        save({ employees: next });
    };
    const durationFromTimes = (tin, tout) => {
        const parse = (s) => {
            if (!s) return null;
            const m = /^(\d{1,2}):(\d{2})$/.exec(s);
            if (!m) return null;
            const h = +m[1], mm = +m[2];
            if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
            return h * 60 + mm;
        };
        const a = parse(tin), b = parse(tout);
        if (a == null || b == null) return null;
        if (b <= a) return null;
        return Math.round(((b - a) / 60) * 100) / 100;
    };

    const updateRow = (idx, key, value) => {
        set((r) => {
            const rows = [...(r.employees || [])];
            const row = { ...rows[idx], [key]: value };
            const locks = empLocks[idx] || { reg: false, ot: false };
            // Time change → auto-fill Reg/OT unless locked
            if ((key === "time_in" || key === "time_out") && !locks.reg && !locks.ot) {
                const dur = durationFromTimes(
                    key === "time_in" ? value : row.time_in,
                    key === "time_out" ? value : row.time_out,
                );
                if (dur != null) {
                    row.regular_hours = Math.min(dur, 8);
                    row.overtime_hours = Math.max(dur - 8, 0);
                } else {
                    row.regular_hours = null;
                    row.overtime_hours = null;
                }
            }
            rows[idx] = row;
            r.employees = rows;
            return r;
        });
    };

    const editRegular = (idx, raw) => {
        const num = raw === "" ? null : Number(raw);
        set((r) => {
            const rows = [...(r.employees || [])];
            const row = { ...rows[idx] };
            if (num != null && num > 8) {
                const excess = num - 8;
                row.regular_hours = 8;
                row.overtime_hours = (Number(row.overtime_hours) || 0) + excess;
                setEmpHint({ idx, text: `Regular capped at 8h — ${excess}h moved to OT` });
                setTimeout(() => setEmpHint((h) => (h && h.idx === idx ? null : h)), 2000);
            } else {
                row.regular_hours = num;
            }
            rows[idx] = row;
            r.employees = rows;
            return r;
        });
        setEmpLocks((prev) => {
            const next = [...prev];
            next[idx] = { ...(next[idx] || { reg: false, ot: false }), reg: raw !== "" };
            return next;
        });
    };

    const editOT = (idx, raw) => {
        const num = raw === "" ? null : Number(raw);
        set((r) => {
            const rows = [...(r.employees || [])];
            rows[idx] = { ...rows[idx], overtime_hours: num };
            r.employees = rows;
            return r;
        });
        setEmpLocks((prev) => {
            const next = [...prev];
            next[idx] = { ...(next[idx] || { reg: false, ot: false }), ot: raw !== "" };
            return next;
        });
    };

    const rowTotal = (row) =>
        (Number(row?.regular_hours) || 0) + (Number(row?.overtime_hours) || 0);

    const persistEmployees = () => { save({ employees: report.employees || [] }); };

    // Sync locks length whenever employees array size changes
    useEffect(() => {
        ensureLocks((report?.employees || []).length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report?.employees?.length]);

    // ---------- Work items ----------
    const workItems = report?.work_items || [];

    const saveWorkItem = async (idx, nextItem, { addAnother }) => {
        // Full-array replacement — send all items with the edited one merged in.
        const currentList = report?.work_items || [];
        const nextList = currentList.map((w, i) => (i === idx ? { ...w, ...nextItem } : w));
        const result = await save({ work_items: nextList });
        if (result.ok) {
            const serverList = result.data.work_items || [];
            const savedItem = serverList[idx];
            toast.success("Work item saved", { duration: 1200 });
            if (addAnother) {
                // Append a fresh blank item and expand it (needs to persist to get an id)
                const appended = [
                    ...serverList,
                    { title: "", location: "", description_of_work: "", materials_used: "" },
                ];
                const r2 = await save({ work_items: appended });
                if (r2.ok) {
                    const list2 = r2.data.work_items || [];
                    const newItem = list2[list2.length - 1];
                    setExpandedItemId(newItem?.id || null);
                    setFocusTitleId(newItem?.id || null);
                }
            } else {
                setExpandedItemId(null);
            }
        }
    };

    const addWorkItem = async () => {
        if (isMobile) {
            // On mobile, create the item server-side (so it has an id) then open sheet.
            const next = [
                ...(report?.work_items || []),
                { title: "", location: "", description_of_work: "", materials_used: "" },
            ];
            const result = await save({ work_items: next });
            if (result.ok) {
                const list = result.data.work_items || [];
                const newItem = list[list.length - 1];
                setSheetItem({ index: list.length - 1, item: newItem, isNew: true });
            }
            return;
        }
        const next = [
            ...(report?.work_items || []),
            { title: "", location: "", description_of_work: "", materials_used: "" },
        ];
        const result = await save({ work_items: next });
        if (result.ok) {
            const list = result.data.work_items || [];
            const newItem = list[list.length - 1];
            setExpandedItemId(newItem?.id || null);
            setFocusTitleId(newItem?.id || null);
        }
    };

    const openSheetForItem = (idx) => {
        const w = (report?.work_items || [])[idx];
        if (w) setSheetItem({ index: idx, item: w, isNew: false });
    };

    const saveFromSheet = async (nextItem, { addAnother }) => {
        const idx = sheetItem?.index;
        if (idx == null) return;
        const currentList = report?.work_items || [];
        const nextList = currentList.map((w, i) => (i === idx ? { ...w, ...nextItem } : w));
        const result = await save({ work_items: nextList });
        if (!result.ok) return;
        const serverList = result.data.work_items || [];
        toast.success("Work item saved", { duration: 1200 });
        if (addAnother) {
            const appended = [
                ...serverList,
                { title: "", location: "", description_of_work: "", materials_used: "" },
            ];
            const r2 = await save({ work_items: appended });
            if (r2.ok) {
                const list2 = r2.data.work_items || [];
                setSheetItem({ index: list2.length - 1, item: list2[list2.length - 1], isNew: true });
            }
        } else {
            setSheetItem(null);
            // Highlight the just-saved item briefly (mobile)
            setTimeout(() => {
                const el = document.querySelector(`[data-testid="work-item-collapsed-${idx}"]`);
                if (el) {
                    el.classList.add("just-saved-glow");
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => el.classList.remove("just-saved-glow"), 850);
                }
            }, 60);
        }
    };

    const deleteFromSheet = async () => {
        if (sheetItem?.index == null) return;
        if (!window.confirm("Delete this work item?")) return;
        const next = (report?.work_items || []).filter((_, i) => i !== sheetItem.index);
        await save({ work_items: next });
        setSheetItem(null);
    };

    const deleteWorkItem = async (idx) => {
        if (!window.confirm("Delete this work item?")) return;
        const next = (report?.work_items || []).filter((_, i) => i !== idx);
        await save({ work_items: next });
        setExpandedItemId(null);
    };

    // ---------- Cover photos ----------
    const onCoverPhotosChange = (nextPhotos) => {
        set((r) => { r.cover_photos = nextPhotos; return r; });
    };

    // ---------- Signature ----------
    const onSignatureEnd = (dataUrl) => {
        setSigDataUrl(dataUrl || "");
        save({ signature_png_base64: dataUrl || null });
    };

    // ---------- Save as Draft ----------
    const saveAsDraft = async () => {
        if (!report) return;
        setManualDraftSaving(true);
        const patch = {
            job_number: report.job_number ?? null,
            location: report.location ?? "",
            report_date: report.report_date ?? null,
            contractor_hours: {
                start: report.contractor_hours?.start || null,
                end: report.contractor_hours?.end || null,
            },
            weather: report.weather ?? "",
            temperature: {
                value: report.temperature?.value == null ? null : Number(report.temperature.value),
                unit: report.temperature?.unit || "F",
            },
            employees: report.employees || [],
            work_items: (report.work_items || []).map((w) => ({
                id: w.id,
                title: w.title,
                location: w.location,
                description_of_work: w.description_of_work,
                materials_used: w.materials_used,
            })),
            pm_email: report.pm_email ?? null,
            cc_emails: report.cc_emails || [],
            certified_checkbox: !!report.certified_checkbox,
            signature_png_base64: report.signature_png_base64 ?? null,
        };
        const result = await save(patch);
        setManualDraftSaving(false);
        if (result.ok) toast.success("Saved as draft", { duration: 2000 });
        else toast.error(result.error || "Failed to save draft", { duration: 2500 });
    };

    // ---------- Certify ----------
    const canCertifyClient = checkbox && !!sigDataUrl;
    const certify = async () => {
        setCertifyErrors({});
        setError("");
        setCertifying(true);
        try {
            const { data } = await api.post(`/reports/${id}/certify`);
            nav(`/reports/${id}`, { state: { emailResult: data.email } });
        } catch (err) {
            const s = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (s === 422 && detail && typeof detail === "object" && detail.errors) {
                setCertifyErrors(detail.errors);
            } else if (s === 409 && detail?.error === "REPORT_LOCKED") {
                nav(`/reports/${id}`, { replace: true });
            } else {
                setError(formatApiError(err));
            }
        } finally {
            setCertifying(false);
        }
    };

    const fieldErrText = (key) =>
        certifyErrors[key] ? (
            <div className="err" data-testid={`error-${key}`}>{certifyErrors[key]}</div>
        ) : null;

    const savedTag = useMemo(() => {
        if (saving) return "Saving…";
        if (!savedAt) return null;
        return `Last saved ${savedAt.toLocaleTimeString(undefined, { hour12: false })}`;
    }, [saving, savedAt]);

    if (!report) {
        return (
            <AppShell>
                <div className="glass-card text-[13px] text-slate-500">Loading…</div>
            </AppShell>
        );
    }

    const workItemErrorsExist = Object.keys(certifyErrors).some((k) => k.startsWith("work_items"));

    return (
        <AppShell>
            {/* Shared datalists for autocomplete */}
            <datalist id="pm-emails-list">
                {(suggestions.recent_pm_emails || []).map((e) => <option key={e} value={e} />)}
            </datalist>
            <datalist id="employee-names-list">
                {(suggestions.recent_employee_names || []).map((n) => <option key={n} value={n} />)}
            </datalist>

            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <Link
                        to="/reports"
                        className="btn btn-secondary btn-sm btn-icon"
                        aria-label="Back to reports"
                        data-testid="edit-back-button"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                    </Link>
                    <div>
                        <div className="flex items-center gap-2 text-[13px] text-slate-500">
                            <span className="chip chip-draft">Draft</span>
                            <span>· id {id.slice(0, 8)}</span>
                        </div>
                        <h1 className="text-3xl font-semibold text-slate-900 mt-1" data-testid="edit-page-title">
                            Edit report
                        </h1>
                    </div>
                </div>
                <div
                    className={`text-[13px] ${
                        saving ? "text-slate-600" : savedAt ? "text-emerald-700" : "text-slate-400"
                    }`}
                    data-testid="edit-save-indicator"
                >
                    {savedTag || "Autosaves on blur"}
                </div>
            </div>

            {error && (
                <div className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2 mb-4" data-testid="edit-error">
                    {error}
                </div>
            )}

            <div className="flex flex-col gap-5">
                {/* JOB INFO */}
                <SectionCard title="Job Info" subtitle={report.day_of_week || ""} collapsible defaultOpen testId="section-job-info">
                    {/* Row 1: Job Number (small) + Location (wide) */}
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="md:w-[140px] shrink-0">
                            <label className="label label-required" htmlFor="job_number">Job number</label>
                            <input
                                id="job_number"
                                className="input"
                                inputMode="numeric"
                                pattern="[0-9]{3,8}"
                                maxLength={8}
                                value={report.job_number || ""}
                                onChange={(e) => {
                                    // Strip non-digits and clamp to 8
                                    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                                    set((r) => { r.job_number = digits; return r; });
                                }}
                                onBlur={(e) => saveField(["job_number"], e.target.value || null)}
                                data-testid="field-job-number"
                            />
                            {(() => {
                                const v = (report.job_number || "").trim();
                                if (v && !/^[0-9]{3,8}$/.test(v)) {
                                    return (
                                        <div className="text-[12px] text-red-600 mt-1" data-testid="field-job-number-hint">
                                            3–8 digits
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            {fieldErrText("job_number")}
                        </div>
                        <div className="md:flex-1 min-w-0">
                            <label className="label" htmlFor="location">Location</label>
                            <input
                                id="location"
                                className="input"
                                placeholder="Site address, jobsite name, etc."
                                value={report.location || ""}
                                onChange={(e) => set((r) => { r.location = e.target.value; return r; })}
                                onBlur={(e) => saveField(["location"], e.target.value || "")}
                                autoCapitalize="sentences"
                                data-testid="field-location"
                            />
                        </div>
                    </div>

                    {/* Row 2: Date + Day of Week + Weather + Temperature */}
                    <div className="flex flex-col md:flex-row gap-4 mt-4">
                        <div className="md:w-[180px] shrink-0">
                            <label className="label label-required" htmlFor="report_date">Date</label>
                            <input
                                id="report_date"
                                type="date"
                                className="input"
                                value={report.report_date || ""}
                                onChange={(e) => set((r) => {
                                    r.report_date = e.target.value;
                                    r.day_of_week = dayFromDate(e.target.value);
                                    return r;
                                })}
                                onBlur={(e) => {
                                    saveField(["report_date"], e.target.value || null);
                                    if (report.weather || report.temperature?.value != null) {
                                        setDateChangedSinceWeather(true);
                                    }
                                }}
                                data-testid="field-report-date"
                            />
                            {fieldErrText("report_date")}
                        </div>
                        <div className="md:w-[140px] shrink-0">
                            <label className="label" htmlFor="day_of_week">Day of week</label>
                            <input
                                id="day_of_week"
                                className="input"
                                value={report.day_of_week || dayFromDate(report.report_date) || ""}
                                disabled
                                data-testid="field-day-of-week"
                            />
                        </div>
                        <div className="md:flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                                <label className="label" htmlFor="weather">Weather</label>
                                <button
                                    type="button"
                                    onClick={() => fetchWeather({ manual: true })}
                                    disabled={weatherFetching}
                                    className="text-[12px] text-emerald-700 hover:text-emerald-800 disabled:opacity-50 inline-flex items-center gap-1"
                                    data-testid="weather-refresh-link"
                                >
                                    <MapPin size={12} strokeWidth={2} />
                                    {weatherFetching ? "Fetching…" : "Refresh weather"}
                                </button>
                            </div>
                            <input
                                id="weather"
                                className="input"
                                value={report.weather || ""}
                                onChange={(e) => set((r) => { r.weather = e.target.value; return r; })}
                                onBlur={(e) => saveField(["weather"], e.target.value)}
                                data-testid="field-weather"
                            />
                            {geoDenied && (
                                <div className="text-[12px] text-slate-500 mt-1" data-testid="weather-geo-denied-note">
                                    Location permission denied — enter weather manually.
                                </div>
                            )}
                            {dateChangedSinceWeather && !geoDenied && (
                                <div className="text-[12px] text-slate-500 mt-1" data-testid="weather-date-changed-note">
                                    Report date changed — refresh to update weather.
                                </div>
                            )}
                            {fieldErrText("weather")}
                        </div>
                        <div className="md:w-[180px] shrink-0">
                            <label className="label" htmlFor="temperature">Temperature</label>
                            <div className="flex items-stretch gap-1.5">
                                <input
                                    id="temperature"
                                    type="number"
                                    inputMode="decimal"
                                    step="0.1"
                                    className="input flex-1 min-w-0"
                                    value={report.temperature?.value == null ? "" : report.temperature.value}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        const v = raw === "" ? null : Number(raw);
                                        set((r) => {
                                            r.temperature = { value: v, unit: r.temperature?.unit || "F" };
                                            return r;
                                        });
                                    }}
                                    onBlur={() => save({
                                        temperature: {
                                            value: report.temperature?.value == null ? null : Number(report.temperature.value),
                                            unit: report.temperature?.unit || "F",
                                        },
                                    })}
                                    data-testid="field-temperature-value"
                                />
                                <div className="tab-pill-group" role="tablist">
                                    {["F", "C"].map((u) => (
                                        <button
                                            key={u}
                                            type="button"
                                            className="tab-pill"
                                            data-active={report.temperature?.unit === u}
                                            onClick={() => {
                                                const next = {
                                                    value: report.temperature?.value == null ? null : Number(report.temperature.value),
                                                    unit: u,
                                                };
                                                set((r) => { r.temperature = next; return r; });
                                                save({ temperature: next });
                                            }}
                                            data-testid={`field-temperature-unit-${u}`}
                                        >
                                            °{u}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {fieldErrText("temperature.value")}
                            {fieldErrText("temperature.unit")}
                        </div>
                    </div>
                </SectionCard>

                {/* WORK INFO */}
                <SectionCard
                    title="Work Info"
                    subtitle={
                        workItems.length === 0
                            ? "Add each distinct piece of work performed today."
                            : `${workItems.length} work item${workItems.length === 1 ? "" : "s"}`
                    }
                    collapsible
                    defaultOpen
                    testId="section-work-info"
                    right={
                        <button
                            type="button"
                            className="btn btn-primary btn-sm desktop-only"
                            onClick={addWorkItem}
                            data-testid="work-item-add"
                        >
                            <Plus size={14} strokeWidth={2.5} />
                            Add work item
                        </button>
                    }
                >
                    {workItemErrorsExist && (
                        <div className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2 mb-3" data-testid="work-items-errors">
                            One or more work items are missing required fields.
                        </div>
                    )}
                    {workItems.length === 0 ? (
                        <div className="text-center py-8 text-[14px] text-slate-500" data-testid="work-items-empty">
                            No work items yet. Click <span className="font-medium">+ Add work item</span> to start.
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {workItems.map((w, i) => (
                                <WorkItemCard
                                    key={w.id || i}
                                    reportId={id}
                                    item={w}
                                    index={i}
                                    isDraft
                                    expanded={!isMobile && expandedItemId === w.id}
                                    focusTitle={focusTitleId === w.id}
                                    onExpand={() => {
                                        if (isMobile) openSheetForItem(i);
                                        else { setExpandedItemId(w.id); setFocusTitleId(null); }
                                    }}
                                    onCollapse={() => setExpandedItemId(null)}
                                    onSave={(next, opts) => saveWorkItem(i, next, opts)}
                                    onDelete={() => deleteWorkItem(i)}
                                />
                            ))}
                        </div>
                    )}
                    {fieldErrText("work_items")}
                </SectionCard>

                {/* SITE PHOTOS */}
                <SectionCard title="Site Photos" subtitle="Cover photos for the whole report" collapsible defaultOpen={false} testId="section-site-photos">
                    <PhotoGallery
                        reportId={id}
                        photos={report.cover_photos || []}
                        onChange={onCoverPhotosChange}
                        testIdPrefix="cover-photos"
                        mobile={isMobile}
                    />
                </SectionCard>

                {/* EMPLOYEES */}
                <SectionCard
                    title="Employees"
                    subtitle={
                        report.crew_total_hours != null
                            ? `Crew total ${report.crew_total_hours} h`
                            : "Crew total —"
                    }
                    collapsible
                    defaultOpen={false}
                    testId="section-employees"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="label" htmlFor="contractor_start">Contractor work hours — start</label>
                            <input
                                id="contractor_start"
                                type="time"
                                className="input"
                                value={report.contractor_hours?.start || ""}
                                onChange={(e) => set((r) => {
                                    r.contractor_hours = { ...(r.contractor_hours || {}), start: e.target.value };
                                    return r;
                                })}
                                onBlur={() => save({
                                    contractor_hours: {
                                        start: report.contractor_hours?.start || null,
                                        end: report.contractor_hours?.end || null,
                                    },
                                })}
                                data-testid="field-contractor-start"
                            />
                            {fieldErrText("contractor_hours.start")}
                        </div>
                        <div>
                            <label className="label" htmlFor="contractor_end">Contractor work hours — end</label>
                            <input
                                id="contractor_end"
                                type="time"
                                className="input"
                                value={report.contractor_hours?.end || ""}
                                onChange={(e) => set((r) => {
                                    r.contractor_hours = { ...(r.contractor_hours || {}), end: e.target.value };
                                    return r;
                                })}
                                onBlur={() => save({
                                    contractor_hours: {
                                        start: report.contractor_hours?.start || null,
                                        end: report.contractor_hours?.end || null,
                                    },
                                })}
                                data-testid="field-contractor-end"
                            />
                            {fieldErrText("contractor_hours.end")}
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 mb-3">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setPositionsModalOpen(true)}
                            data-testid="employees-manage-positions"
                        >
                            <Settings size={14} strokeWidth={2} />
                            Manage positions
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => addRow()}
                            data-testid="employees-add-row"
                        >
                            <Plus size={14} strokeWidth={2.5} />
                            Add row
                        </button>
                    </div>
                    {fieldErrText("employees")}

                    {/* Mobile: stacked employee cards */}
                    <div className="mobile-only flex flex-col gap-3" data-testid="employees-mobile-list">
                        {(report.employees || []).length === 0 && (
                            <div className="text-center text-[13px] italic text-slate-500">
                                No employees yet. Add a row.
                            </div>
                        )}
                        {(report.employees || []).map((row, idx) => (
                            <div key={idx} className="emp-mobile-card" data-testid={`employee-mobile-row-${idx}`}>
                                <div>
                                    <label className="label">Position</label>
                                    <select
                                        className="select"
                                        value={row.position || ""}
                                        onChange={(e) => updateRow(idx, "position", e.target.value)}
                                        onBlur={persistEmployees}
                                        data-testid={`employee-position-m-${idx}`}
                                    >
                                        <option value="">—</option>
                                        {row.position && !positions.find((p) => p.name === row.position) && (
                                            <option value={row.position}>{row.position} (removed)</option>
                                        )}
                                        {positions.map((p) => (
                                            <option key={p.id} value={p.name}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="label">Employee name</label>
                                    <input
                                        className="input"
                                        list="employee-names-list"
                                        value={row.name || ""}
                                        onChange={(e) => updateRow(idx, "name", e.target.value)}
                                        onBlur={persistEmployees}
                                        autoCapitalize="words"
                                        data-testid={`employee-name-m-${idx}`}
                                    />
                                </div>
                                <div className="time-row">
                                    <div>
                                        <label className="label">Time in</label>
                                        <input
                                            type="time"
                                            className="input"
                                            value={row.time_in || ""}
                                            onChange={(e) => updateRow(idx, "time_in", e.target.value)}
                                            onBlur={persistEmployees}
                                            data-testid={`employee-time-in-m-${idx}`}
                                        />
                                    </div>
                                    <div>
                                        <label className="label">Time out</label>
                                        <input
                                            type="time"
                                            className="input"
                                            value={row.time_out || ""}
                                            onChange={(e) => updateRow(idx, "time_out", e.target.value)}
                                            onBlur={persistEmployees}
                                            data-testid={`employee-time-out-m-${idx}`}
                                        />
                                    </div>
                                </div>
                                <div className="time-row">
                                    <div>
                                        <label className="label">Regular hours</label>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min="0" step="0.25"
                                            className="input tabular-nums"
                                            value={row.regular_hours == null ? "" : row.regular_hours}
                                            onChange={(e) => editRegular(idx, e.target.value)}
                                            onBlur={persistEmployees}
                                            data-testid={`employee-regular-m-${idx}`}
                                        />
                                        {empHint && empHint.idx === idx && (
                                            <div className="text-[11px] text-amber-700 mt-1">{empHint.text}</div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="label">OT hours</label>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min="0" step="0.25"
                                            className="input tabular-nums"
                                            value={row.overtime_hours == null ? "" : row.overtime_hours}
                                            onChange={(e) => editOT(idx, e.target.value)}
                                            onBlur={persistEmployees}
                                            data-testid={`employee-overtime-m-${idx}`}
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="text-[13px] text-slate-600 tabular-nums">
                                        Total: <span className="font-medium">
                                            {rowTotal(row) > 0 ? `${rowTotal(row)} h` : "—"}
                                        </span>
                                        {row.warning && (
                                            <span className="err ml-2">{row.warning}</span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="text-[13px] text-red-600 inline-flex items-center gap-1"
                                        onClick={() => removeRow(idx)}
                                        data-testid={`employee-remove-m-${idx}`}
                                    >
                                        <Trash2 size={12} strokeWidth={2} />
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="btn btn-secondary w-full"
                            onClick={addRow}
                            data-testid="employees-add-row-m"
                        >
                            <Plus size={14} strokeWidth={2.5} />
                            Add employee
                        </button>
                    </div>

                    <div className="overflow-x-auto desktop-only">
                        <table className="g-table" data-testid="employees-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "16%" }}>Position</th>
                                    <th style={{ width: "22%" }}>Employee name</th>
                                    <th style={{ width: "11%" }}>Time in</th>
                                    <th style={{ width: "11%" }}>Time out</th>
                                    <th style={{ width: "12%" }} className="text-right">Regular</th>
                                    <th style={{ width: "12%" }} className="text-right">OT</th>
                                    <th style={{ width: "14%" }} className="text-right">Total</th>
                                    <th style={{ width: "1%" }}> </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(report.employees || []).length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center text-slate-500 text-[13px] italic">
                                            No employees yet. Add a row.
                                        </td>
                                    </tr>
                                )}
                                {(report.employees || []).map((row, idx) => (
                                    <tr key={idx} data-testid={`employee-row-${idx}`}>
                                        <td>
                                            <select
                                                className="select"
                                                value={row.position || ""}
                                                onChange={(e) => updateRow(idx, "position", e.target.value)}
                                                onBlur={persistEmployees}
                                                data-testid={`employee-position-${idx}`}
                                            >
                                                <option value="">—</option>
                                                {row.position && !positions.find((p) => p.name === row.position) && (
                                                    <option value={row.position}>{row.position} (removed)</option>
                                                )}
                                                {positions.map((p) => (
                                                    <option key={p.id} value={p.name}>{p.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                className="input"
                                                list="employee-names-list"
                                                value={row.name || ""}
                                                onChange={(e) => updateRow(idx, "name", e.target.value)}
                                                onBlur={persistEmployees}
                                                data-testid={`employee-name-${idx}`}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="time"
                                                className="input"
                                                value={row.time_in || ""}
                                                onChange={(e) => updateRow(idx, "time_in", e.target.value)}
                                                onBlur={persistEmployees}
                                                data-testid={`employee-time-in-${idx}`}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="time"
                                                className="input"
                                                value={row.time_out || ""}
                                                onChange={(e) => updateRow(idx, "time_out", e.target.value)}
                                                onBlur={persistEmployees}
                                                data-testid={`employee-time-out-${idx}`}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                min="0" step="0.25"
                                                className="input text-right tabular-nums"
                                                value={row.regular_hours == null ? "" : row.regular_hours}
                                                onChange={(e) => editRegular(idx, e.target.value)}
                                                onBlur={persistEmployees}
                                                data-testid={`employee-regular-${idx}`}
                                            />
                                            {empHint && empHint.idx === idx && (
                                                <div className="text-[11px] text-amber-700 mt-1" data-testid={`employee-hint-${idx}`}>
                                                    {empHint.text}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                min="0" step="0.25"
                                                className="input text-right tabular-nums"
                                                value={row.overtime_hours == null ? "" : row.overtime_hours}
                                                onChange={(e) => editOT(idx, e.target.value)}
                                                onBlur={persistEmployees}
                                                data-testid={`employee-overtime-${idx}`}
                                            />
                                        </td>
                                        <td className="tabular-nums text-right pr-3" data-testid={`employee-total-hours-${idx}`}>
                                            {rowTotal(row) > 0 ? rowTotal(row) : "—"}
                                            {row.warning && (
                                                <div className="err mt-1" data-testid={`employee-warning-${idx}`}>{row.warning}</div>
                                            )}
                                            {["position", "name", "time_in", "time_out"]
                                                .map((k) => certifyErrors[`employees.${idx}.${k}`])
                                                .filter(Boolean)
                                                .map((msg, i) => (
                                                    <div key={i} className="err mt-1" data-testid={`employee-cert-error-${idx}-${i}`}>
                                                        {msg}
                                                    </div>
                                                ))}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm btn-icon"
                                                onClick={() => removeRow(idx)}
                                                aria-label="Delete row"
                                                data-testid={`employee-delete-${idx}`}
                                            >
                                                <Trash2 size={13} strokeWidth={2} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={4} className="text-right text-slate-600">Crew total</td>
                                    <td className="tabular-nums text-right" data-testid="employees-crew-regular">
                                        {report.crew_regular_hours != null ? report.crew_regular_hours : "—"}
                                    </td>
                                    <td className="tabular-nums text-right" data-testid="employees-crew-overtime">
                                        {report.crew_overtime_hours != null ? report.crew_overtime_hours : "—"}
                                    </td>
                                    <td className="tabular-nums text-right pr-3" data-testid="employees-crew-total">
                                        {report.crew_total_hours != null ? `${report.crew_total_hours} h` : "—"}
                                    </td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </SectionCard>

                {/* CERTIFICATION */}
                <SectionCard title="Certification" subtitle="Lock this report" collapsible defaultOpen={false} testId="section-certification">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="label" htmlFor="pm_email">PM email</label>
                            <input
                                id="pm_email"
                                type="email"
                                className="input"
                                list="pm-emails-list"
                                value={report.pm_email || ""}
                                onChange={(e) => set((r) => { r.pm_email = e.target.value; return r; })}
                                onBlur={(e) => saveField(["pm_email"], e.target.value || null)}
                                data-testid="field-pm-email"
                            />
                            {fieldErrText("pm_email")}
                        </div>
                        <div className="md:col-span-2">
                            <label className="label" htmlFor="cc_emails">CC (optional)</label>
                            <CCChipsInput
                                value={report.cc_emails || []}
                                onChange={(next) => {
                                    set((r) => { r.cc_emails = next; return r; });
                                    save({ cc_emails: next });
                                }}
                                suggestions={suggestions.recent_cc_emails || []}
                                max={5}
                                testId="cc-chips"
                            />
                            {Object.keys(certifyErrors)
                                .filter((k) => k.startsWith("cc_emails"))
                                .map((k, i) => (
                                    <div key={i} className="err" data-testid={`error-${k}`}>
                                        {certifyErrors[k]}
                                    </div>
                                ))}
                        </div>
                        <div className="flex items-start gap-3 md:pt-8">
                            <input
                                id="certify_checkbox"
                                type="checkbox"
                                checked={checkbox}
                                onChange={(e) => {
                                    setCheckbox(e.target.checked);
                                    save({ certified_checkbox: e.target.checked });
                                }}
                                className="mt-1 w-4 h-4 rounded accent-emerald-500"
                                data-testid="certify-checkbox"
                            />
                            <label htmlFor="certify_checkbox" className="text-[14px] leading-snug text-slate-700" data-testid="certify-statement">
                                I certify that the information contained in this Daily Field Report is true and accurate to the best of my knowledge.
                            </label>
                        </div>
                        <div className="md:col-span-2">{fieldErrText("certified_checkbox")}</div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <SignaturePad
                            ref={sigRef}
                            initialDataUrl={sigDataUrl}
                            onEnd={onSignatureEnd}
                            data-testid="signature-pad"
                        />
                        <div className="flex flex-col justify-between gap-2">
                            <div className="text-[13px] text-slate-500 leading-relaxed">
                                Certify locks the report, generates the signed PDF, and emails the PM. This can't be undone.
                            </div>
                            {fieldErrText("signature_png_base64")}
                            {Object.keys(certifyErrors).length > 0 && (
                                <div className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2" data-testid="certify-errors-summary">
                                    {Object.keys(certifyErrors).length} field{Object.keys(certifyErrors).length === 1 ? "" : "s"} need attention. See red messages above.
                                </div>
                            )}
                            <div className="flex flex-wrap items-center justify-end gap-3 mt-1 desktop-only">
                                <div
                                    className={`text-[12px] ${
                                        saving ? "text-slate-600" : savedAt ? "text-emerald-700" : "text-slate-400"
                                    }`}
                                    data-testid="edit-save-indicator-bottom"
                                >
                                    {savedTag || "Autosaves on blur"}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={saveAsDraft}
                                    disabled={manualDraftSaving || saving}
                                    data-testid="save-as-draft-button"
                                >
                                    <Save size={16} strokeWidth={2} />
                                    {manualDraftSaving ? "Saving…" : "Save as draft"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-accent"
                                    disabled={!canCertifyClient || certifying}
                                    onClick={certify}
                                    data-testid="certify-button"
                                >
                                    <Check size={16} strokeWidth={2.5} />
                                    {certifying ? "Certifying…" : "Certify report"}
                                </button>
                            </div>
                        </div>
                    </div>
                </SectionCard>
            </div>

            <ManagePositionsModal
                open={positionsModalOpen}
                onClose={() => setPositionsModalOpen(false)}
                onChanged={loadPositions}
            />

            {/* Mobile-only: sticky action bar spacer + FAB + sticky bar + WorkItemSheet */}
            {isMobile && !sheetItem && (
                <>
                    <div className="actionbar-spacer" aria-hidden />
                    <button
                        type="button"
                        className="mobile-fab"
                        onClick={addWorkItem}
                        aria-label="Add work item"
                        data-testid="mobile-fab-add-work-item"
                    >
                        <Plus size={22} strokeWidth={2.5} />
                    </button>
                    <div className="mobile-actionbar" data-testid="mobile-actionbar">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={saveAsDraft}
                            disabled={manualDraftSaving || saving}
                            data-testid="save-as-draft-button-m"
                        >
                            <Save size={16} strokeWidth={2} />
                            {manualDraftSaving ? "Saving…" : "Save draft"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-accent"
                            onClick={certify}
                            disabled={!canCertifyClient || certifying}
                            data-testid="certify-button-m"
                        >
                            <Check size={16} strokeWidth={2.5} />
                            {certifying ? "Certifying…" : "Certify"}
                        </button>
                    </div>
                </>
            )}
            {isMobile && sheetItem && (
                <WorkItemSheet
                    open
                    reportId={id}
                    item={sheetItem.item}
                    isNew={sheetItem.isNew}
                    onSave={saveFromSheet}
                    onDelete={deleteFromSheet}
                    onClose={() => setSheetItem(null)}
                />
            )}
        </AppShell>
    );
};

function pathToPatch(path, value) {
    if (!Array.isArray(path) || path.length === 0) return {};
    const [head, ...rest] = path;
    if (rest.length === 0) return { [head]: value };
    return { [head]: pathToPatch(rest, value) };
}

export default ReportEditPage;
